/**
 * Landing ranges — the pure rules behind `RangeField` (Round 7, Task 6).
 *
 * Stephanie: *"do not allow the user to add landing groups that exceed the
 * number of landings in that group. Currently, I can keep adding landing
 * ranges even when the values are the same, such as From 2 to 2, repeated
 * multiple times… make sure the user can type in a custom range… when there
 * are multiple ranges, ensure they do not overlap."* Those are all judgements
 * about a document — where a new row starts, whether one can be added at all,
 * and how far an edited bound may travel before it steps on a landing another
 * row already owns — and a judgement about a document belongs in
 * `src/ui/state`, not in the `.tsx` that draws the boxes. `RangeField` renders
 * what this module decides; it does not decide anything itself.
 *
 * Every function here treats `rows` as landings 1…max carved into ascending,
 * non-overlapping spans, left to right — the ideal. `freeSpans`, `seedRange`
 * and `commitRange` all sort a working copy before deciding anything, because
 * the array `rows` arrives in is NOT guaranteed to match that ideal: a survey
 * saved before this task existed has a `WallRange[]` a surveyor could drag
 * anywhere with no clipping at all, so it can be out of order and/or
 * genuinely overlapping, and `QuestionField` passes it straight through with
 * no sort of its own. `commitRange`'s neighbours are therefore found by
 * LANDING ORDER — the nearest row (by `fromLanding`) either side of the
 * edited row's own position among every OTHER row — never by adjacent array
 * index. `RangeField` still keeps freshly-added rows spliced into landing
 * order (so the screen reads top to bottom the way the landings run), and
 * `commitRange` preserves whatever order it is handed, but neither is load-
 * bearing for correctness any more.
 *
 * `commitRange` also returns the SAME array reference it was given whenever
 * an edit resolves to no change — refused entirely, or clamped/dragged/
 * clipped back to exactly the value already stored. A caller can compare the
 * result to its input with `===` to know whether anything needs writing, so
 * a field that is focused and then left untouched — or an edit that clamps
 * back to what was already there — costs nothing.
 */

export interface Span {
  fromLanding: number;
  toLanding: number;
}

/**
 * Landings 1…max not covered by any row, as ascending spans; unbounded above
 * when `max` is undefined (then the last span is open: `toLanding = Infinity`).
 */
export function freeSpans(rows: readonly Span[], max?: number): Span[] {
  const sorted = [...rows].sort((a, b) => a.fromLanding - b.fromLanding);
  const spans: Span[] = [];
  let cursor = 1;

  for (const row of sorted) {
    if (row.fromLanding > cursor) {
      spans.push({ fromLanding: cursor, toLanding: row.fromLanding - 1 });
    }
    cursor = Math.max(cursor, row.toLanding + 1);
  }

  if (max === undefined) {
    spans.push({ fromLanding: cursor, toLanding: Infinity });
  } else if (cursor <= max) {
    spans.push({ fromLanding: cursor, toLanding: max });
  }

  return spans;
}

/**
 * Where a new row starts: the first free span, clipped to its own end (or
 * `max`, or its start when open-ended). `undefined` when nothing is free —
 * the caller reads that as "no `+ Add` button" (D-015: a dead control is a
 * puzzle, so the button is absent, not disabled).
 */
export function seedRange(rows: readonly Span[], max?: number): Span | undefined {
  const [first] = freeSpans(rows, max);
  if (!first) return undefined;
  // An open span (max unknown) has no end to seed up to — start a single
  // landing rather than proposing an unbounded row.
  const toLanding = Number.isFinite(first.toLanding) ? first.toLanding : first.fromLanding;
  return { fromLanding: first.fromLanding, toLanding };
}

/** true when a row can still be added (`seedRange` !== undefined). */
export function canAddRange(rows: readonly Span[], max?: number): boolean {
  return seedRange(rows, max) !== undefined;
}

/** A landing bound is a positive whole number, capped at `max` when known. */
function clampBound(value: number, max: number | undefined): number {
  const positive = Math.max(1, Math.trunc(value));
  return max === undefined ? positive : Math.min(positive, max);
}

/**
 * The edited row, clamped to [1,max], the other bound dragged, then clipped
 * to the free space between its LANDING-ORDER neighbours (see the module
 * header — not necessarily `rows[index ± 1]`); returns the SAME `rows`
 * reference, unchanged, when the edit leaves nothing, or when it resolves to
 * exactly the row already stored. Row order is preserved.
 */
export function commitRange<R extends Span>(
  rows: readonly R[],
  index: number,
  patch: Partial<Span>,
  max?: number,
): R[] {
  const current = rows[index];
  if (!current) return rows as R[];

  const merged: Span = { fromLanding: current.fromLanding, toLanding: current.toLanding };

  if (patch.fromLanding !== undefined && Number.isFinite(patch.fromLanding)) {
    merged.fromLanding = clampBound(patch.fromLanding, max);
    // fromLanding <= toLanding is a schema invariant (WallRangeSchema); drag
    // the other bound along rather than writing a row the store would refuse.
    if (merged.fromLanding > merged.toLanding) merged.toLanding = merged.fromLanding;
  }
  if (patch.toLanding !== undefined && Number.isFinite(patch.toLanding)) {
    merged.toLanding = clampBound(patch.toLanding, max);
    if (merged.toLanding < merged.fromLanding) merged.fromLanding = merged.toLanding;
  }

  // This row's true spatial neighbours: sort every row (this one included) by
  // `fromLanding` — stable, so two rows that start on the same landing keep
  // their original array order — find where THIS row's original position
  // landed in that order, and take whichever rows fall immediately either
  // side of it. Array position plays no part, so an already-saved survey
  // whose rows are out of order, or genuinely overlapping, is still handled:
  // a row that already overlaps its landing-order neighbour gets clipped
  // back to that neighbour's boundary on any edit — the overlap can only
  // shrink from there, never widen, and nothing throws.
  const order = rows
    .map((row, position) => ({ row, position }))
    .sort((a, b) => a.row.fromLanding - b.row.fromLanding);
  const at = order.findIndex((entry) => entry.position === index);
  const prev = at > 0 ? order[at - 1]?.row : undefined;
  const next = at < order.length - 1 ? order[at + 1]?.row : undefined;

  const lowerBound = prev ? prev.toLanding + 1 : 1;
  const upperBound = next ? next.fromLanding - 1 : (max ?? merged.toLanding);

  merged.fromLanding = Math.max(merged.fromLanding, lowerBound);
  merged.toLanding = Math.min(merged.toLanding, upperBound);

  if (merged.fromLanding > merged.toLanding) {
    // Nothing left between the neighbours for this edit — refuse it rather
    // than write an overlap or an inverted row.
    return rows as R[];
  }

  if (merged.fromLanding === current.fromLanding && merged.toLanding === current.toLanding) {
    // The edit resolves to exactly what was already stored — nothing to
    // write. Same reference back, so a caller can skip the onChange.
    return rows as R[];
  }

  const out = [...rows];
  out[index] = { ...current, ...merged } as R;
  return out;
}
